import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Chip,
  Avatar,
  AvatarGroup,
  Divider,
  Tabs,
  Tab,
  TextareaAutosize,
  Menu,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  ListItemIcon,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  PersonAdd as PersonAddIcon,
  Label as LabelIcon,
  Checklist as ChecklistIcon,
  Attachment as AttachmentIcon,
  Comment as CommentIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import {
  kanbanService,
  KanbanCard,
  KanbanComment,
  KanbanChecklist,
  KanbanChecklistItem,
  KanbanAttachment,
  KanbanLabel,
} from '../../services/kanbanService';
import axios from 'axios';
import { API_CONFIG } from '../../config/api';
import { getApiUrl, getBackendBaseUrl } from '../../config/environment';
import { FileUpload } from '../ui/FileUpload';

interface CardModalProps {
  open: boolean;
  onClose: () => void;
  cardId: string;
}

const CardModal: React.FC<CardModalProps> = ({ open, onClose, cardId }) => {
  const { boardId } = useParams<{ boardId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';
  const [tab, setTab] = useState(0);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState('');
  const [newComment, setNewComment] = useState('');
  const [newChecklistTitle, setNewChecklistTitle] = useState('');
  const [newChecklistItem, setNewChecklistItem] = useState<{ [key: string]: string }>({});
  const [labelMenuAnchor, setLabelMenuAnchor] = useState<null | HTMLElement>(null);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState('#61BD4F');
  const [clientMenuAnchor, setClientMenuAnchor] = useState<null | HTMLElement>(null);
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dueDateValue, setDueDateValue] = useState('');
  const [tempDueDateValue, setTempDueDateValue] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [uploadKey, setUploadKey] = useState(0); // Para resetar o FileUpload após upload

  // Buscar card completo
  const { data: card, isLoading, refetch } = useQuery({
    queryKey: ['kanban-card', cardId],
    queryFn: () => kanbanService.getCard(cardId),
    enabled: open && !!cardId,
    refetchOnWindowFocus: false,
    // Refetch quando o cardId mudar
    refetchOnMount: true,
  });

  // Buscar labels do board
  const { data: labels } = useQuery({
    queryKey: ['kanban-labels', boardId],
    queryFn: () => kanbanService.getLabels(boardId!),
    enabled: open && !!boardId,
  });

  // Buscar clientes
  const { data: clientsData } = useQuery({
    queryKey: ['clients', clientSearchQuery],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: '1',
        limit: '50',
        ...(clientSearchQuery && { search: clientSearchQuery }),
      });
      const response = await axios.get(`${API_CONFIG.BASE_URL}/clients?${params}`);
      return response.data.success ? response.data.data : [];
    },
    enabled: open && Boolean(clientMenuAnchor),
  });

  // Mutations
  const updateCardMutation = useMutation({
    mutationFn: (data: Partial<KanbanCard>) => kanbanService.updateCard(cardId, data),
    onSuccess: async (response) => {
      console.log('Card atualizado com sucesso:', response);
      // Invalidar e refetch para garantir que os dados estão atualizados
      await queryClient.invalidateQueries({ queryKey: ['kanban-card', cardId] });
      await queryClient.invalidateQueries({ queryKey: ['kanban-board', boardId] });
      // Aguardar um pouco antes de refetch para garantir que o banco atualizou
      setTimeout(async () => {
        await refetch();
      }, 100);
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: (text: string) => kanbanService.addComment(cardId, text),
    onSuccess: () => {
      // Invalidar queries para forçar refetch
      queryClient.invalidateQueries({ queryKey: ['kanban-card', cardId] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board', boardId] });
    },
  });

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => kanbanService.deleteComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-card', cardId] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board', boardId] });
      refetch();
    },
  });

  const deleteCardMutation = useMutation({
    mutationFn: (id: string) => kanbanService.deleteCard(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-board', boardId] });
      onClose();
      // Opcional: navegar de volta para a lista de boards se necessário
    },
  });

  const createChecklistMutation = useMutation({
    mutationFn: (title: string) => kanbanService.createChecklist(cardId, { title }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-card', cardId] });
      setNewChecklistTitle('');
    },
  });

  const updateChecklistItemMutation = useMutation({
    mutationFn: ({
      checklistId,
      itemId,
      data,
    }: {
      checklistId: string;
      itemId: string;
      data: { is_completed?: boolean; text?: string };
    }) => kanbanService.updateChecklistItem(checklistId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-card', cardId] });
    },
  });

  const addChecklistItemMutation = useMutation({
    mutationFn: ({ checklistId, text }: { checklistId: string; text: string }) =>
      kanbanService.addChecklistItem(checklistId, text),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['kanban-card', cardId] });
      setNewChecklistItem((prev) => ({ ...prev, [variables.checklistId]: '' }));
    },
  });

  const deleteChecklistItemMutation = useMutation({
    mutationFn: ({ checklistId, itemId }: { checklistId: string; itemId: string }) =>
      kanbanService.deleteChecklistItem(checklistId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-card', cardId] });
      refetch();
    },
  });

  const deleteChecklistMutation = useMutation({
    mutationFn: (checklistId: string) => kanbanService.deleteChecklist(checklistId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-card', cardId] });
      refetch();
    },
  });

  const uploadAttachmentMutation = useMutation({
    mutationFn: (file: File) => kanbanService.uploadAttachment(cardId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-card', cardId] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board', boardId] });
      refetch();
      setUploadingAttachment(false);
      // Resetar o FileUpload após upload bem-sucedido
      setUploadKey(prev => prev + 1);
    },
    onError: () => {
      setUploadingAttachment(false);
    },
  });

  const deleteAttachmentMutation = useMutation({
    mutationFn: (attachmentId: string) => kanbanService.deleteAttachment(attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-card', cardId] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board', boardId] });
      refetch();
    },
  });

  const addLabelMutation = useMutation({
    mutationFn: (labelId: string) => kanbanService.addCardLabel(cardId, labelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-card', cardId] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board', boardId] });
    },
  });

  const removeLabelMutation = useMutation({
    mutationFn: (labelId: string) => kanbanService.removeCardLabel(cardId, labelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-card', cardId] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board', boardId] });
    },
  });

  const createLabelMutation = useMutation({
    mutationFn: (data: { name: string; color: string }) =>
      kanbanService.createLabel(boardId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-labels', boardId] });
      queryClient.invalidateQueries({ queryKey: ['kanban-card', cardId] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board', boardId] });
      setNewLabelName('');
      setLabelMenuAnchor(null);
    },
  });

  // Se labels são IDs, transformar em objetos (DEVE estar antes dos returns)
  const cardLabels = React.useMemo(() => {
    if (!card || !card.labels || card.labels.length === 0) return [];
    
    const firstLabel = card.labels[0];
    
    // Se já são objetos com id, usar diretamente
    if (firstLabel && typeof firstLabel === 'object' && 'id' in firstLabel) {
      return card.labels as any[];
    }
    
    // Se são IDs, buscar os objetos
    return (card.labels as string[])
      .map((labelId) => labels?.find((l: KanbanLabel) => l.id === labelId))
      .filter(Boolean);
  }, [card?.labels, labels]);

  // Sincronizar titleValue quando card mudar (DEVE estar antes dos returns)
  React.useEffect(() => {
    if (card && !editingTitle) {
      setTitleValue(card.title || '');
    }
  }, [card?.title, editingTitle]);

  // Sincronizar descriptionValue quando card mudar (DEVE estar antes dos returns)
  React.useEffect(() => {
    if (card && !editingDescription) {
      setDescriptionValue(card.description || '');
    }
  }, [card?.description, editingDescription]);

  // Sincronizar dueDateValue quando card mudar
  React.useEffect(() => {
    console.log('Card due_date mudou:', card?.due_date);
    if (card?.due_date) {
      try {
        // A data vem como ISO string (ex: "2025-11-08T00:00:00.000Z")
        // Como salvamos com +1 dia, precisamos subtrair 1 dia ao exibir no calendário
        // para mostrar a data que o usuário realmente selecionou
        const dateString = card.due_date;
        
        // Se for uma string ISO, extrair apenas a parte da data (antes do 'T')
        let dateOnly = dateString;
        if (dateString.includes('T')) {
          dateOnly = dateString.split('T')[0];
        }
        
        // Validar formato YYYY-MM-DD
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (dateRegex.test(dateOnly)) {
          // Subtrair um dia para mostrar a data original que o usuário selecionou
          // (já que salvamos com +1 dia)
          const [year, month, day] = dateOnly.split('-').map(Number);
          const date = new Date(year, month - 1, day);
          date.setDate(date.getDate() - 1); // Subtrair um dia
          
          const adjustedYear = date.getFullYear();
          const adjustedMonth = String(date.getMonth() + 1).padStart(2, '0');
          const adjustedDay = String(date.getDate()).padStart(2, '0');
          const adjustedDateString = `${adjustedYear}-${adjustedMonth}-${adjustedDay}`;
          
          console.log('Data formatada para calendário (com ajuste de -1 dia):', { 
            original: dateOnly, 
            adjusted: adjustedDateString 
          });
          setDueDateValue(adjustedDateString);
          if (!showDatePicker) {
            setTempDueDateValue(adjustedDateString);
          }
        } else {
          // Fallback: tentar parsear como Date e extrair a data UTC
          const date = new Date(card.due_date);
          if (!isNaN(date.getTime())) {
            // Extrair a data UTC e subtrair um dia
            const year = date.getUTCFullYear();
            const month = date.getUTCMonth();
            const day = date.getUTCDate();
            const adjustedDate = new Date(Date.UTC(year, month, day));
            adjustedDate.setUTCDate(adjustedDate.getUTCDate() - 1);
            
            const adjustedYear = adjustedDate.getUTCFullYear();
            const adjustedMonth = String(adjustedDate.getUTCMonth() + 1).padStart(2, '0');
            const adjustedDay = String(adjustedDate.getUTCDate()).padStart(2, '0');
            const dateStringFormatted = `${adjustedYear}-${adjustedMonth}-${adjustedDay}`;
            console.log('Data formatada (fallback UTC com ajuste):', dateStringFormatted);
            setDueDateValue(dateStringFormatted);
            if (!showDatePicker) {
              setTempDueDateValue(dateStringFormatted);
            }
          } else {
            console.warn('Data inválida recebida:', card.due_date);
            setDueDateValue('');
            if (!showDatePicker) {
              setTempDueDateValue('');
            }
          }
        }
      } catch (error) {
        console.error('Erro ao processar data:', error, card.due_date);
        setDueDateValue('');
        if (!showDatePicker) {
          setTempDueDateValue('');
        }
      }
    } else {
      console.log('Nenhuma due_date no card');
      setDueDateValue('');
      if (!showDatePicker) {
        setTempDueDateValue('');
      }
    }
  }, [card?.due_date, showDatePicker]);

  // Sincronizar tempDueDateValue quando abrir o date picker
  React.useEffect(() => {
    if (showDatePicker) {
      setTempDueDateValue(dueDateValue);
    }
  }, [showDatePicker]);

  const handleSaveDueDate = async () => {
    try {
      if (tempDueDateValue) {
        // Formatar data preservando o dia correto
        // tempDueDateValue vem como 'YYYY-MM-DD' do input date
        // Workaround: adicionar um dia para compensar problema de timezone
        const [year, month, day] = tempDueDateValue.split('-').map(Number);
        
        // Criar data e adicionar um dia
        const selectedDate = new Date(year, month - 1, day);
        selectedDate.setDate(selectedDate.getDate() + 1);
        
        // Extrair o novo dia, mês e ano após adicionar um dia
        const newYear = selectedDate.getFullYear();
        const newMonth = selectedDate.getMonth() + 1;
        const newDay = selectedDate.getDate();
        
        // Criar string ISO como meia-noite UTC do dia seguinte
        // Formato: YYYY-MM-DDTHH:mm:ss.sssZ (meia-noite UTC)
        const isoString = `${newYear}-${String(newMonth).padStart(2, '0')}-${String(newDay).padStart(2, '0')}T00:00:00.000Z`;
        
        console.log('Salvando data de vencimento (com offset de +1 dia):', { 
          original: tempDueDateValue, 
          year,
          month,
          day,
          newYear,
          newMonth,
          newDay,
          isoString,
          // Verificar como a data será interpretada
          parsedDate: new Date(isoString).toISOString(),
          localString: new Date(isoString).toLocaleDateString('pt-BR')
        });
        
        await updateCardMutation.mutateAsync({ 
          due_date: isoString 
        });
        setDueDateValue(tempDueDateValue);
      } else {
        // Remover data - enviar null explicitamente
        console.log('Removendo data de vencimento');
        await updateCardMutation.mutateAsync({ due_date: null });
        setDueDateValue('');
        setTempDueDateValue('');
      }
    } catch (error) {
      console.error('Erro ao salvar data:', error);
    } finally {
      setShowDatePicker(false);
    }
  };

  const handleCancelDueDate = () => {
    setTempDueDateValue(dueDateValue);
    setShowDatePicker(false);
  };

  const handleClearDueDate = async () => {
    try {
      console.log('Limpando data de vencimento');
      await updateCardMutation.mutateAsync({ due_date: null });
      setDueDateValue('');
      setTempDueDateValue('');
      setShowDatePicker(false);
    } catch (error) {
      console.error('Erro ao limpar data:', error);
    }
  };

  if (isLoading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Typography>Carregando...</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  if (!card) {
    return null;
  }

  const handleUpdateTitle = () => {
    if (titleValue.trim() && titleValue !== card.title) {
      updateCardMutation.mutate({ title: titleValue });
    }
    setEditingTitle(false);
  };

  const handleUpdateDescription = () => {
    if (descriptionValue !== (card.description || '')) {
      updateCardMutation.mutate({ description: descriptionValue });
    }
    setEditingDescription(false);
  };

  const handleStartEditingDescription = () => {
    setDescriptionValue(card.description || '');
    setEditingDescription(true);
  };

  const handleAddComment = () => {
    if (!newComment.trim()) return;
    addCommentMutation.mutate(newComment, {
      onSuccess: () => {
        setNewComment('');
        // Forçar refetch imediato dos dados do card
        refetch();
      },
      onError: (error: any) => {
        console.error('Erro ao adicionar comentário:', error);
        // TODO: Mostrar toast de erro
      },
    });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      handleUploadAttachment(file);
    }
    // Limpar input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadAttachment = async (file: File) => {
    setUploadingAttachment(true);
    uploadAttachmentMutation.mutate(file);
  };

  const handlePasteImage = async (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        event.preventDefault();
        const file = item.getAsFile();
        if (file) {
          // Criar um File com nome apropriado
          const blob = file.slice(0, file.size, file.type);
          const imageFile = new File([blob], `pasted-image-${Date.now()}.png`, { type: file.type });
          handleUploadAttachment(imageFile);
        }
        break;
      }
    }
  };

  const handleToggleChecklistItem = (checklistId: string, itemId: string, isCompleted: boolean) => {
    updateChecklistItemMutation.mutate({
      checklistId,
      itemId,
      data: { is_completed: !isCompleted },
    });
  };

  const handleAddChecklistItem = (checklistId: string) => {
    const text = newChecklistItem[checklistId];
    if (!text?.trim()) return;
    addChecklistItemMutation.mutate({ checklistId, text });
  };

  return (
    <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
            },
          },
        }}
        sx={{
          '& .MuiDialog-paper': {
            maxHeight: '90vh',
            height: '90vh',
            background: isDark 
              ? 'rgba(31, 41, 55, 0.95) !important' 
              : 'rgba(255, 255, 255, 0.98) !important',
            backdropFilter: 'blur(20px) !important',
            WebkitBackdropFilter: 'blur(20px) !important',
            border: isDark 
              ? '1px solid rgba(139, 92, 246, 0.3) !important' 
              : '1px solid rgba(139, 92, 246, 0.15) !important',
            borderRadius: '16px !important',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(139, 92, 246, 0.1) !important',
            overflow: 'hidden',
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.7) !important',
            backdropFilter: 'blur(4px) !important',
            WebkitBackdropFilter: 'blur(4px) !important',
          },
        }}
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            height: '90vh',
            background: isDark 
              ? 'rgba(31, 41, 55, 0.95)' 
              : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: isDark 
              ? '1px solid rgba(139, 92, 246, 0.3)' 
              : '1px solid rgba(139, 92, 246, 0.15)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(139, 92, 246, 0.1)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogContent sx={{ 
          p: 0, 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100%',
          background: 'transparent',
          '&.MuiDialogContent-root': {
            padding: 0,
          },
        }}>
          {/* Header */}
          <Box
            sx={{
              p: 3,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              background: isDark 
                ? 'rgba(31, 41, 55, 0.6)' 
                : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              borderBottom: isDark 
                ? '1px solid rgba(139, 92, 246, 0.3)' 
                : '1px solid rgba(139, 92, 246, 0.2)',
            }}
          >
            <Box sx={{ flex: 1, pr: 2 }}>
              {editingTitle ? (
                <TextField
                  fullWidth
                  value={titleValue}
                  onChange={(e) => setTitleValue(e.target.value)}
                  onBlur={handleUpdateTitle}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleUpdateTitle();
                    }
                  }}
                  autoFocus
                  variant="standard"
                />
              ) : (
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{ 
                    mb: 1, 
                    cursor: 'pointer',
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                    '&:hover': {
                      opacity: 0.8,
                    },
                  }}
                  onClick={() => {
                    setTitleValue(card.title);
                    setEditingTitle(true);
                  }}
                >
                  {card.title}
                  <EditIcon sx={{ 
                    ml: 1, 
                    fontSize: 18, 
                    verticalAlign: 'middle',
                    color: 'rgba(139, 92, 246, 0.7)',
                  }} />
                </Typography>
              )}

              <Typography variant="body2" color="text.secondary">
                {card.description && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {card.description}
                    </Typography>
                  </Box>
                )}
              </Typography>
            </Box>
            <IconButton 
              onClick={onClose}
              sx={{
                color: theme.palette.text.primary,
                '&:hover': {
                  backgroundColor: isDark 
                    ? 'rgba(139, 92, 246, 0.2)' 
                    : 'rgba(139, 92, 246, 0.1)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider sx={{ 
            borderColor: isDark 
              ? 'rgba(139, 92, 246, 0.2)' 
              : 'rgba(139, 92, 246, 0.1)' 
          }} />

          {/* Content */}
          <Box sx={{ 
            flex: 1, 
            overflow: 'auto', 
            p: 3,
            background: isDark 
              ? 'rgba(31, 41, 55, 0.4)' 
              : 'rgba(255, 255, 255, 0.6)',
          }}>
            <Box display="flex" gap={3}>
              {/* Main Content */}
              <Box sx={{ flex: 1 }}>
                {/* Description */}
                <Box mb={3}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: theme.palette.text.primary,
                        fontWeight: 600,
                      }}
                    >
                      Descrição
                    </Typography>
                    {!editingDescription && (
                      <IconButton 
                        size="small" 
                        onClick={handleStartEditingDescription}
                        sx={{
                          color: 'rgba(139, 92, 246, 0.7)',
                          '&:hover': {
                            backgroundColor: isDark 
                              ? 'rgba(139, 92, 246, 0.2)' 
                              : 'rgba(139, 92, 246, 0.1)',
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  {editingDescription ? (
                    <TextareaAutosize
                      minRows={4}
                      value={descriptionValue}
                      onChange={(e) => setDescriptionValue(e.target.value)}
                      onBlur={handleUpdateDescription}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setDescriptionValue(card.description || '');
                          setEditingDescription(false);
                        }
                      }}
                      autoFocus
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: isDark 
                          ? '1px solid rgba(139, 92, 246, 0.2)' 
                          : '1px solid rgba(139, 92, 246, 0.1)',
                        borderRadius: '8px',
                        fontFamily: 'inherit',
                        fontSize: '14px',
                        backgroundColor: isDark 
                          ? 'rgba(31, 41, 55, 0.5)' 
                          : 'rgba(255, 255, 255, 0.8)',
                        color: theme.palette.text.primary,
                        resize: 'vertical',
                      }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        cursor: 'pointer',
                        minHeight: '60px',
                        p: 1,
                        whiteSpace: 'pre-wrap',
                        borderRadius: '8px',
                        '&:hover': {
                          background: isDark 
                            ? 'rgba(139, 92, 246, 0.1)' 
                            : 'rgba(139, 92, 246, 0.05)',
                        },
                      }}
                      onClick={handleStartEditingDescription}
                    >
                      {card.description || 'Adicionar descrição...'}
                    </Typography>
                  )}
                </Box>

                {/* Checklists */}
                <Box mb={3}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <ChecklistIcon sx={{ color: theme.palette.text.primary }} />
                    <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>Checklists</Typography>
                  </Box>
                  {card.checklists?.map((checklist: KanbanChecklist) => (
                    <Box 
                      key={checklist.id} 
                      mb={2}
                      sx={{
                        p: 2,
                        border: isDark 
                          ? '1px solid rgba(139, 92, 246, 0.2)' 
                          : '1px solid rgba(139, 92, 246, 0.1)',
                        borderRadius: '8px',
                        backgroundColor: isDark 
                          ? 'rgba(31, 41, 55, 0.5)' 
                          : 'rgba(255, 255, 255, 0.6)',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                          {checklist.title}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (window.confirm('Tem certeza que deseja excluir esta checklist?')) {
                              deleteChecklistMutation.mutate(checklist.id);
                            }
                          }}
                          sx={{
                            color: theme.palette.error.main,
                            '&:hover': {
                              backgroundColor: theme.palette.error.main + '20',
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <List dense>
                        {checklist.kanban_checklist_items?.map((item: KanbanChecklistItem) => (
                          <ListItem 
                            key={item.id} 
                            disablePadding
                            sx={{
                              '&:hover .delete-item-button': {
                                opacity: 1,
                              },
                            }}
                          >
                            <ListItemIcon>
                              <Checkbox
                                checked={item.is_completed}
                                onChange={() =>
                                  handleToggleChecklistItem(
                                    checklist.id,
                                    item.id,
                                    item.is_completed
                                  )
                                }
                                size="small"
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={item.text}
                              sx={{
                                textDecoration: item.is_completed ? 'line-through' : 'none',
                                color: item.is_completed ? 'text.secondary' : 'text.primary',
                                flex: 1,
                              }}
                            />
                            <IconButton
                              size="small"
                              className="delete-item-button"
                              onClick={() => {
                                deleteChecklistItemMutation.mutate({
                                  checklistId: checklist.id,
                                  itemId: item.id,
                                });
                              }}
                              sx={{
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                color: theme.palette.error.main,
                                '&:hover': {
                                  backgroundColor: theme.palette.error.main + '20',
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItem>
                        ))}
                        <ListItem>
                          <TextField
                            size="small"
                            placeholder="Adicionar item"
                            value={newChecklistItem[checklist.id] || ''}
                            onChange={(e) =>
                              setNewChecklistItem((prev) => ({
                                ...prev,
                                [checklist.id]: e.target.value,
                              }))
                            }
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleAddChecklistItem(checklist.id);
                              }
                            }}
                            fullWidth
                          />
                        </ListItem>
                      </List>
                    </Box>
                  ))}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const title = prompt('Título da checklist:');
                      if (title) {
                        createChecklistMutation.mutate(title);
                      }
                    }}
                  >
                    Adicionar Checklist
                  </Button>
                </Box>

                {/* Attachments */}
                <Box mb={3}>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <AttachmentIcon sx={{ color: theme.palette.text.primary }} />
                    <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>Anexos</Typography>
                  </Box>
                  
                  {/* Componente de Upload Moderno */}
                  <Box sx={{ mb: 3 }}>
                    <FileUpload
                      key={uploadKey}
                      onUpload={(files) => {
                        files.forEach((file) => {
                          handleUploadAttachment(file);
                        });
                      }}
                      uploading={uploadingAttachment}
                    />
                  </Box>

                  {/* Lista de Anexos Existentes */}
                  {card.attachments && card.attachments.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                      {card.attachments.map((attachment: KanbanAttachment) => {
                        // Usar URL direta do R2 se disponível, senão usar rota proxy do backend
                        let imageUrl: string;
                        
                        if (attachment.file_url && attachment.file_url.startsWith('http')) {
                          // URL do R2, usar diretamente
                            imageUrl = attachment.file_url;
                          } else {
                          // URL local, usar rota proxy do backend
                          const filename = attachment.file_url?.split('/').pop() || attachment.file_name || '';
                          const timestamp = attachment.created_at ? new Date(attachment.created_at).getTime() : Date.now();
                          imageUrl = `${getApiUrl()}/kanban/attachments/${filename}?t=${timestamp}`;
                        }
                        
                        return (
                          <Box
                            key={attachment.id}
                            sx={{
                              position: 'relative',
                              border: isDark 
                                ? '1px solid rgba(139, 92, 246, 0.2)' 
                                : '1px solid rgba(139, 92, 246, 0.1)',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              '&:hover .delete-attachment-button': {
                                opacity: 1,
                              },
                            }}
                          >
                            <Box
                              component="img"
                              src={imageUrl}
                              alt={attachment.file_name}
                              crossOrigin="anonymous"
                              loading="lazy"
                              sx={{
                                width: 150,
                                height: 150,
                                objectFit: 'cover',
                                display: 'block',
                                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                              }}
                            />
                            <IconButton
                              size="small"
                              className="delete-attachment-button"
                              onClick={() => {
                                if (window.confirm('Tem certeza que deseja excluir este anexo?')) {
                                  deleteAttachmentMutation.mutate(attachment.id);
                                }
                              }}
                              sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                p: 0.5,
                                textAlign: 'center',
                                fontSize: '10px',
                                color: theme.palette.text.secondary,
                                maxWidth: 150,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {attachment.file_name}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>

                {/* Comments */}
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <CommentIcon sx={{ color: theme.palette.text.primary }} />
                    <Typography variant="h6" sx={{ color: theme.palette.text.primary }}>Comentários</Typography>
                  </Box>
                  <Box mb={2}>
                    <TextareaAutosize
                      minRows={3}
                      placeholder="Escreva um comentário... (Ctrl+V para colar imagem)"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onPaste={handlePasteImage}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                          e.preventDefault();
                          handleAddComment();
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: isDark 
                          ? '1px solid rgba(139, 92, 246, 0.2)' 
                          : '1px solid rgba(139, 92, 246, 0.1)',
                        borderRadius: '8px',
                        fontFamily: 'inherit',
                        fontSize: '14px',
                        marginBottom: '8px',
                        backgroundColor: isDark 
                          ? 'rgba(31, 41, 55, 0.5)' 
                          : 'rgba(255, 255, 255, 0.8)',
                        color: theme.palette.text.primary,
                        resize: 'vertical',
                      }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Pressione Ctrl+Enter (ou Cmd+Enter no Mac) para salvar | Ctrl+V para colar imagem
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || addCommentMutation.isPending}
                      sx={{ mb: 2 }}
                    >
                      {addCommentMutation.isPending ? 'Salvando...' : 'Salvar Comentário'}
                    </Button>
                  </Box>
                  {card.comments && Array.isArray(card.comments) && card.comments.length > 0 ? (
                    card.comments.map((comment: any) => (
                      <Box 
                        key={comment.id} 
                        mb={2} 
                        p={2}
                        sx={{
                          backgroundColor: isDark 
                            ? 'rgba(31, 41, 55, 0.5)' 
                            : 'rgba(255, 255, 255, 0.6)',
                          borderRadius: '8px',
                          border: isDark 
                            ? '1px solid rgba(139, 92, 246, 0.2)' 
                            : '1px solid rgba(139, 92, 246, 0.1)',
                          position: 'relative',
                          '&:hover .delete-comment-button': {
                            opacity: 1,
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ mb: 0.5, color: theme.palette.text.primary, wordBreak: 'break-word' }}>
                              {comment.text}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {comment.created_at && format(new Date(comment.created_at), "dd 'de' MMM 'às' HH:mm", {
                                locale: ptBR,
                              })}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            className="delete-comment-button"
                            onClick={() => {
                              if (window.confirm('Tem certeza que deseja excluir este comentário?')) {
                                deleteCommentMutation.mutate(comment.id);
                              }
                            }}
                            sx={{
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              color: theme.palette.error.main,
                              '&:hover': {
                                backgroundColor: theme.palette.error.main + '20',
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      Nenhum comentário ainda. Seja o primeiro a comentar!
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Sidebar */}
              <Box sx={{ 
                width: 200, 
                borderLeft: isDark 
                  ? '1px solid rgba(139, 92, 246, 0.2)' 
                  : '1px solid rgba(139, 92, 246, 0.1)',
                pl: 2,
              }}>
                <Typography variant="subtitle2" gutterBottom sx={{ color: theme.palette.text.primary }}>
                  Ações
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  {/* Cliente */}
                  <Button
                    startIcon={<BusinessIcon />}
                    fullWidth
                    onClick={(e) => setClientMenuAnchor(e.currentTarget)}
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                    }}
                  >
                    {card.client ? card.client.company_name : 'Atribuir Cliente'}
                  </Button>

                  {/* Menu de Cliente */}
                  <Menu
                    anchorEl={clientMenuAnchor}
                    open={Boolean(clientMenuAnchor)}
                    onClose={() => {
                      setClientMenuAnchor(null);
                      setClientSearchQuery('');
                    }}
                    PaperProps={{
                      sx: {
                        maxWidth: 350,
                        width: '100%',
                        p: 1,
                        backgroundColor: isDark ? theme.palette.background.paper : undefined,
                      },
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ p: 1, fontWeight: 600 }}>
                      Atribuir Cliente
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    
                    {/* Campo de busca */}
                    <TextField
                      size="small"
                      placeholder="Buscar cliente..."
                      value={clientSearchQuery}
                      onChange={(e) => setClientSearchQuery(e.target.value)}
                      fullWidth
                      sx={{ mb: 1 }}
                      autoFocus
                    />

                    {/* Lista de clientes */}
                    {clientsData && clientsData.length > 0 ? (
                      <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                        <MenuItem
                          onClick={() => {
                            updateCardMutation.mutate({ client_id: undefined });
                            setClientMenuAnchor(null);
                            setClientSearchQuery('');
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Remover cliente
                          </Typography>
                        </MenuItem>
                        <Divider sx={{ my: 1 }} />
                        {clientsData.map((client: any) => (
                          <MenuItem
                            key={client.id}
                            onClick={() => {
                              updateCardMutation.mutate({ client_id: client.id });
                              setClientMenuAnchor(null);
                              setClientSearchQuery('');
                            }}
                            selected={card.client_id === client.id}
                          >
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: card.client_id === client.id ? 600 : 400 }}>
                                {client.company_name}
                              </Typography>
                              {client.contact_name && (
                                <Typography variant="caption" color="text.secondary">
                                  {client.contact_name}
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 1, fontStyle: 'italic' }}>
                        {clientSearchQuery ? 'Nenhum cliente encontrado' : 'Digite para buscar clientes'}
                      </Typography>
                    )}
                  </Menu>

                  {/* Labels */}
                  <Button
                    startIcon={<LabelIcon />}
                    fullWidth
                    onClick={(e) => setLabelMenuAnchor(e.currentTarget)}
                  >
                    Labels
                  </Button>
                  
                  {/* Menu de Labels */}
                  <Menu
                    anchorEl={labelMenuAnchor}
                    open={Boolean(labelMenuAnchor)}
                    onClose={() => {
                      setLabelMenuAnchor(null);
                      setNewLabelName('');
                    }}
                    PaperProps={{
                      sx: {
                        maxWidth: 300,
                        width: '100%',
                        p: 1,
                        backgroundColor: isDark ? theme.palette.background.paper : undefined,
                      },
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ p: 1, fontWeight: 600 }}>
                      Etiquetas
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                    
                    {/* Labels existentes */}
                    {labels && labels.length > 0 ? (
                      <Box sx={{ maxHeight: 200, overflowY: 'auto', mb: 1 }}>
                        {labels.map((label: KanbanLabel) => {
                          const isSelected = cardLabels.some((cl) => cl?.id === label.id);
                          return (
                            <MenuItem
                              key={label.id}
                              onClick={() => {
                                if (isSelected) {
                                  removeLabelMutation.mutate(label.id);
                                } else {
                                  addLabelMutation.mutate(label.id);
                                }
                              }}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <Checkbox
                                checked={isSelected}
                                size="small"
                                sx={{
                                  color: label.color,
                                  '&.Mui-checked': {
                                    color: label.color,
                                  },
                                }}
                              />
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: 1,
                                  backgroundColor: label.color,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography variant="body2">{label.name}</Typography>
                            </MenuItem>
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 1, fontStyle: 'italic' }}>
                        Nenhuma etiqueta criada. Crie uma abaixo.
                      </Typography>
                    )}
                    
                    <Divider sx={{ my: 1 }} />
                    
                    {/* Criar nova label */}
                    <Box sx={{ p: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        Criar nova etiqueta
                      </Typography>
                      <TextField
                        size="small"
                        placeholder="Nome da etiqueta"
                        value={newLabelName}
                        onChange={(e) => setNewLabelName(e.target.value)}
                        fullWidth
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        {[
                          '#61BD4F', '#F2D600', '#FF9F1A', '#EB5A46',
                          '#C377E0', '#0079BF', '#00C2E0', '#51E898',
                          '#FF78CB', '#344563', '#B3BAC5',
                        ].map((color: string) => (
                          <Box
                            key={color}
                            onClick={() => setNewLabelColor(color)}
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: 1,
                              backgroundColor: color,
                              cursor: 'pointer',
                              border: newLabelColor === color ? '2px solid #333' : '2px solid transparent',
                              '&:hover': {
                                opacity: 0.8,
                              },
                            }}
                          />
                        ))}
                      </Box>
                      <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        disabled={!newLabelName.trim() || createLabelMutation.isPending}
                        onClick={() => {
                          createLabelMutation.mutate({
                            name: newLabelName,
                            color: newLabelColor,
                          });
                        }}
                      >
                        {createLabelMutation.isPending ? 'Criando...' : 'Criar'}
                      </Button>
                    </Box>
                  </Menu>

                  {/* Due Date */}
                  <Box>
                    <Button
                      startIcon={<ScheduleIcon />}
                      fullWidth
                      onClick={() => setShowDatePicker(!showDatePicker)}
                      sx={{
                        justifyContent: 'flex-start',
                      }}
                    >
                      {card.due_date 
                        ? `Data de Vencimento: ${(() => {
                            const dateStr = card.due_date;
                            let dateOnly = dateStr;
                            if (dateStr.includes('T')) {
                              dateOnly = dateStr.split('T')[0];
                            }
                            const [year, month, day] = dateOnly.split('-').map(Number);
                            const date = new Date(Date.UTC(year, month - 1, day));
                            return format(date, 'dd/MM/yyyy', { locale: ptBR });
                          })()}`
                        : 'Data de Vencimento'}
                    </Button>
                    {showDatePicker && (
                      <Box sx={{ 
                        mt: 1, 
                        p: 1, 
                        border: isDark 
                          ? '1px solid rgba(139, 92, 246, 0.2)' 
                          : '1px solid rgba(139, 92, 246, 0.1)', 
                        borderRadius: '8px',
                        backgroundColor: isDark 
                          ? 'rgba(31, 41, 55, 0.5)' 
                          : 'rgba(255, 255, 255, 0.6)',
                      }}>
                        <TextField
                          type="date"
                          value={tempDueDateValue}
                          onChange={(e) => setTempDueDateValue(e.target.value)}
                          size="small"
                          fullWidth
                          label="Selecione a data"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{
                            max: '9999-12-31',
                          }}
                          sx={{ mb: 1 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleCancelDueDate}
                            sx={{ flex: 1 }}
                          >
                            Cancelar
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleSaveDueDate}
                            disabled={updateCardMutation.isPending}
                            sx={{ flex: 1 }}
                          >
                            {updateCardMutation.isPending ? 'Salvando...' : 'Concluir'}
                          </Button>
                        </Box>
                        {tempDueDateValue && (
                          <Button
                            variant="text"
                            size="small"
                            color="error"
                            onClick={handleClearDueDate}
                            fullWidth
                            sx={{ mt: 1 }}
                          >
                            Limpar Data
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* Members */}
                  <Button
                    startIcon={<PersonAddIcon />}
                    fullWidth
                    onClick={() => {
                      // TODO: Abrir menu de membros
                    }}
                  >
                    Membros
                  </Button>

                  <Divider sx={{ my: 1 }} />

                  {/* Delete */}
                  <Button
                    startIcon={<DeleteIcon />}
                    fullWidth
                    color="error"
                    disabled={deleteCardMutation.isPending}
                    onClick={() => {
                      if (window.confirm('Tem certeza que deseja excluir este cartão? Esta ação não pode ser desfeita.')) {
                        deleteCardMutation.mutate(cardId);
                      }
                    }}
                  >
                    {deleteCardMutation.isPending ? 'Excluindo...' : 'Excluir'}
                  </Button>
                </Box>

                {/* Labels */}
                {cardLabels && cardLabels.length > 0 && (
                  <Box mt={3}>
                    <Typography variant="subtitle2" gutterBottom>
                      Labels
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                      {cardLabels.map((label: KanbanLabel | undefined) =>
                        label ? (
                          <Chip
                            key={label.id}
                            label={label.name}
                            size="small"
                            sx={{
                              backgroundColor: label.color,
                              color: 'white',
                              '&:hover': {
                                opacity: 0.8,
                              },
                            }}
                            onDelete={() => removeLabelMutation.mutate(label.id)}
                          />
                        ) : null
                      )}
                    </Box>
                  </Box>
                )}

                {/* Due Date */}
                {card.due_date && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Data de Vencimento
                    </Typography>
                    <Chip
                      icon={<ScheduleIcon />}
                      label={(() => {
                        const dateStr = card.due_date;
                        let dateOnly = dateStr;
                        if (dateStr.includes('T')) {
                          dateOnly = dateStr.split('T')[0];
                        }
                        const [year, month, day] = dateOnly.split('-').map(Number);
                        const date = new Date(Date.UTC(year, month - 1, day));
                        return format(date, 'dd/MM/yyyy', {
                          locale: ptBR,
                        });
                      })()}
                      size="small"
                      color={
                        (() => {
                          const dateStr = card.due_date;
                          let dateOnly = dateStr;
                          if (dateStr.includes('T')) {
                            dateOnly = dateStr.split('T')[0];
                          }
                          const [year, month, day] = dateOnly.split('-').map(Number);
                          const dueDate = new Date(Date.UTC(year, month - 1, day));
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return dueDate < today && !card.is_completed;
                        })()
                          ? 'error'
                          : 'default'
                      }
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
  );
};

export default CardModal;
